import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion } from 'framer-motion';
import { DeleteOutlined, DownOutlined, DragOutlined, EditOutlined, EyeOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, PlusOutlined } from '@ant-design/icons';
import { KanbanDragHandleIcon } from '@/components/ui/KanbanDragHandleIcon';
import { Dropdown, Form, Image, Input, theme, Checkbox } from 'antd';
import { DndContext, DragOverlay, PointerSensor, useDroppable, useDraggable, useSensor, useSensors } from '@dnd-kit/core';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import Markdown from '@/components/ui/Markdown';
import MultipleButton from '@/components/ui/MultipleButton';
import TaskAttachmentTile from '@/components/module/projects/task/TaskAttachmentTile';
import StatusBadge from '@/components/ui/StatusBadge';
import AppModal from '@/pages/inside/tenant/AppModal';
import { SprintTaskCard } from '@/components/sprint/SprintTaskCard';
import QASubmissionGroupCard from '@/components/module/projects/qa_tab/QASubmissionGroupCard';
import {
    formatSprintDeadlineRange,
    getSprintDisplayLabel,
    formatSprintStatusLabel,
    getSprintAttachmentList,
    getSprintDescriptionText,
    getSprintProgressPercent,
    getSprintTaskApiId,
    isTaskReturnedByQa,
    isLikelyImageAttachmentUrl,
    normalizePhaseLinkUrl,
    resolveAttachmentUrl,
    getAttachmentFileExtension,
} from '@hooks/projects/projectHelpers';
import { useSprintKanbanSyncHorizontalScroll } from '@hooks/projects/useProjectSprints';
import { useMatchMedia } from '@hooks/useMatchMedia';
import {
    SprintKanbanFill,
    KanbanThreeColumnRow,
    KanbanColumnPanel,
    KanbanColumnHeader,
    KanbanColumnTitle,
    KanbanColumnCountBadge,
    TaskCardKanbanStatusPillCount,
    KanbanColumnBody,
    KanbanDragDropPlaceholder,
    PanelSectionLabel,
    kanbanDesktopHeaderCellFlexStyle,
    kanbanDesktopBoardRowInnerStyle,
    kanbanDesktopBoardHeaderRowInnerStyle,
} from '@/pages/inside/tenant/style';



const COL = { TODO: 'todo', COMPLETED: 'completed', CONSULTATION: 'consultation', SUBMITTED: 'submitted' };

const StyledLinkButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    color: var(--ant-color-primary, #7c3aed);
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    &:hover {
        text-decoration: underline;
        opacity: 0.85;
    }
`;

const COLUMN_DROP_MESSAGES = {
    [COL.TODO]: 'Drop here to move to Todo',
    [COL.COMPLETED]: 'Drop here when work is completed',
    [COL.CONSULTATION]: 'Drop here for Consultation',
    [COL.SUBMITTED]: 'Drop here to move to Submitted',
};

/** Submit / Move controls on Todo & Completed headers: click opens column jump menu. */
const COLUMN_HEADER_FLOW = {
    [COL.TODO]: {
        label: 'Submit',
        menuKeys: [
            { key: COL.COMPLETED, label: 'Mark As Completed' },
            { key: COL.CONSULTATION, label: 'Consultation' },
            { key: COL.SUBMITTED, label: 'Submitted' },
        ],
    },
    [COL.COMPLETED]: {
        label: 'Move',
        menuKeys: [
            { key: COL.TODO, label: 'Todo' },
            { key: COL.CONSULTATION, label: 'Consultation' },
            { key: COL.SUBMITTED, label: 'Submitted' },
        ],
    },
    [COL.CONSULTATION]: {
        label: 'Move',
        menuKeys: [
            { key: COL.TODO, label: 'Todo' },
            { key: COL.COMPLETED, label: 'Mark As Completed' },
            { key: COL.SUBMITTED, label: 'Submitted' },
        ],
    },
};

function scrollSprintKanbanColumnIntoView(columnId) {
    const el = document.querySelector(`[data-kanban-drop-column="${columnId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
}

const SPRINT_KANBAN_DRAG_CLASS = 'sprint-kanban-dragging';

/** Source row collapse when dragging — fast and clean, gives space back to siblings immediately. */
const KANBAN_SOURCE_SHRINK_DURATION = 0.14;
const KANBAN_SOURCE_SHRINK_EASE = [0.4, 0, 0.2, 1];
/** Float clone: settle-out on drop / cancel before removal */
const KANBAN_FLOAT_EXIT_MS = 220;

/** Shared by Sprints Kanban + QA Kanban — do not blanket-disable `-webkit-user-drag` on all children (breaks the drag handle). */
export const SprintKanbanDragCursorStyle = createGlobalStyle`
    body.${SPRINT_KANBAN_DRAG_CLASS} {
        cursor: grabbing !important;
        user-select: none !important;
    }
    /* Block native drag on images/links; drag handle opts in via [data-kanban-drag-handle] */
    .sprint-kanban-task-drag img,
    .sprint-kanban-task-drag a {
        -webkit-user-drag: none;
    }
    .sprint-kanban-task-drag [data-kanban-drag-handle] {
        cursor: grab;
        -webkit-user-drag: element;
        touch-action: none;
    }
    .sprint-kanban-task-drag [data-kanban-drag-handle]:active {
        cursor: grabbing;
    }
`;

export const LoadMoreCard = styled.button`
    width: 100%;
    margin-top: 12px;
    padding: 12px;
    border-radius: 8px;
    background: var(--ant-color-fill-quaternary, rgba(0, 0, 0, 0.02));
    backdrop-filter: blur(12px);
    border: 1px dashed color-mix(in srgb, var(--ant-color-primary, #7c3aed) 30%, transparent);
    color: var(--ant-color-primary, #7c3aed);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: block;
    text-align: center;
    &:hover {
        background: color-mix(in srgb, var(--ant-color-primary, #7c3aed) 8%, transparent);
        transform: translateY(-1px);
    }
    &:active {
        transform: translateY(0);
    }
`;

/** Reusable 1×1 transparent canvas — browser drag preview is dimmed; we hide it and use a fixed DOM clone instead. */
let transparentDragCanvas;
function getTransparentDragCanvas() {
    if (!transparentDragCanvas) {
        transparentDragCanvas = document.createElement('canvas');
        transparentDragCanvas.width = 1;
        transparentDragCanvas.height = 1;
        transparentDragCanvas.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
        document.body.appendChild(transparentDragCanvas);
    }
    return transparentDragCanvas;
}

/** Collapse + columns: board fills tab shell height; column bodies scroll when content overflows. */
export const SprintKanbanScrollChainStyle = createGlobalStyle`
    .sprint-kanban-root {
        flex: 1 1 0%;
        min-height: 0;
        overflow: hidden;
        margin-bottom: 0;
        padding-bottom: 0;
        --sprint-kanban-sticky-top: calc(52px + 8px + 10px);
    }
    /*
     * Only Sprint Details uses Ant Collapse now (Tasks is a fixed-height flex area).
     * Keep Collapse intrinsic so it doesn't reserve empty space when collapsed.
     */
    .sprint-kanban-root .ant-collapse {
        flex: 0 0 auto !important;
        min-height: 0 !important;
    }
    .sprint-kanban-root .ant-collapse > .ant-collapse-item .ant-collapse-content-box {
        padding-bottom: 0 !important;
    }
    .sprint-kanban-root .ant-collapse-borderless > .ant-collapse-item {
        margin-bottom: 0 !important;
        border-radius: 0 !important;
        border-bottom: none !important;
    }
    /* Ant Collapse adds default header/extra padding — flush Create task to the right gutter */
    .sprint-kanban-root .ant-collapse .ant-collapse-header {
        padding-right: 0 !important;
        padding-inline-end: 0 !important;
    }
    /* Expand arrow alignment: force wrapper + arrow down */
    .sprint-kanban-root .ant-collapse .ant-collapse-header .ant-collapse-expand-icon {
        display: inline-flex !important;
        align-items: center !important;
        align-self: center !important;
        padding-top: 0 !important;
    }
    .sprint-kanban-root .ant-collapse .ant-collapse-header .ant-collapse-arrow {
        position: relative;
        top: 0px;
    }
    .sprint-kanban-root .ant-collapse .ant-collapse-header .ant-collapse-expand-icon .anticon {
        transform: none !important;
    }
    .sprint-kanban-root .ant-collapse .ant-collapse-extra {
        margin-inline-end: 0 !important;
        padding-right: 0 !important;
        padding-inline-end: 0 !important;
    }
    /* Clip collapse motion so the board height stays bounded (tasks must not extend past the tab). */
    .sprint-kanban-root .ant-motion-collapse,
    .sprint-kanban-root .ant-motion-collapse-legacy {
        overflow: hidden !important;
        min-height: 0 !important;
    }
    .sprint-kanban-root .sprint-kanban-fill-natural {
        flex: 1 1 0% !important;
        min-height: 0 !important;
        overflow: hidden !important;
    }
    .sprint-kanban-root .sprint-kanban-column-row-natural {
        flex: 1 1 0% !important;
        min-height: 0 !important;
        overflow-x: auto;
        overflow-y: hidden;
    }
    /* Kanban header strip */
    .sprint-kanban-root .sprint-kanban-sticky-strip {
        position: static;
        padding-bottom: 0;
        margin-bottom: 0;
        background: transparent;
        /* Remove bottom underline under header strip */
        box-shadow: none;
    }
    .sprint-kanban-root .sprint-kanban-h-scroll-sync {
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: thin;
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        padding: 0;
    }
    /* Header row: keep horizontal scroll + sync with body; hide scrollbar (body keeps thin bar for affordance) */
    .sprint-kanban-root .sprint-kanban-sticky-strip .sprint-kanban-h-scroll-sync {
        scrollbar-width: none;
        -ms-overflow-style: none;
    }
    .sprint-kanban-root .sprint-kanban-sticky-strip .sprint-kanban-h-scroll-sync::-webkit-scrollbar {
        display: none;
        height: 0;
    }
    .sprint-kanban-root .sprint-kanban-desktop-split {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
        min-width: 0;
        margin-bottom: 0;
        padding-bottom: 0;
    }
    .sprint-kanban-root .sprint-kanban-desktop-body-wrap {
        width: 100%;
        min-width: 0;
        overflow: hidden;
        flex: 1 1 0%;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
    .sprint-kanban-root .sprint-kanban-desktop-body-wrap .sprint-kanban-h-scroll-sync {
        flex: 1 1 0%;
        min-height: 0;
        display: flex !important;
        flex-direction: column !important;
    }
    /* Row of columns: 1 1 0% + min-height 0 lets the row shrink below content so bodies scroll */
    .sprint-kanban-root .sprint-kanban-desktop-board-row {
        flex: 1 1 0% !important;
        min-height: 0 !important;
        overflow: hidden !important;
        align-items: stretch !important;
    }
    @media (min-width: 1025px) {
        .sprint-kanban-root .sprint-kanban-desktop-board-row > [data-kanban-drop-column] {
            display: flex !important;
            flex-direction: column !important;
            flex: 1 1 0% !important;
            min-height: 0 !important;
            max-height: none !important;
            overflow: hidden !important;
            align-self: stretch !important;
        }
    }
    /* Stack columns on narrow screens: row scrolls vertically; each column body can scroll if capped */
    @media (max-width: 1024px) {
        .sprint-kanban-root .sprint-kanban-column-row-natural {
            flex-direction: column;
            flex-wrap: nowrap;
            align-items: stretch;
            align-content: flex-start;
            flex: 1 1 auto !important;
            min-height: 0 !important;
            overflow-x: hidden;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            padding: 0;
            gap: 20px;
            scrollbar-width: thin;
        }
        /* Overlay scrollbar: keep thumb hidden until hover */
        .sprint-kanban-root .sprint-kanban-column-row-natural {
            scrollbar-color: transparent transparent;
        }
        .sprint-kanban-root .sprint-kanban-column-row-natural:hover {
            scrollbar-color: ${({ theme }) =>
        theme.colors.background.default === '#141414'
            ? 'rgba(255,255,255,0.22) transparent'
            : 'rgba(0,0,0,0.28) transparent'};
        }
        .sprint-kanban-root .sprint-kanban-column-row-natural::-webkit-scrollbar {
            width: 6px;
        }
        .sprint-kanban-root .sprint-kanban-column-row-natural::-webkit-scrollbar-track {
            background: transparent;
        }
        .sprint-kanban-root .sprint-kanban-column-row-natural::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 999px;
        }
        .sprint-kanban-root .sprint-kanban-column-row-natural:hover::-webkit-scrollbar-thumb {
            background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.28)'};
        }
        .sprint-kanban-root .sprint-kanban-column-body-natural:not([data-drag-placeholder='true']) {
            flex: 0 1 auto !important;
            min-height: 0 !important;
            max-height: min(55dvh, 520px) !important;
            overflow-x: hidden;
            overflow-y: auto !important;
            overscroll-behavior: contain;
            scrollbar-width: thin;
        }
        .sprint-kanban-root .sprint-kanban-column-body-natural:not([data-drag-placeholder='true']) {
            scrollbar-color: transparent transparent;
        }
        .sprint-kanban-root .sprint-kanban-column-body-natural:not([data-drag-placeholder='true']):hover {
            scrollbar-color: ${({ theme }) =>
        theme.colors.background.default === '#141414'
            ? 'rgba(255,255,255,0.22) transparent'
            : 'rgba(0,0,0,0.28) transparent'};
        }
        .sprint-kanban-root .sprint-kanban-column-body-natural:not([data-drag-placeholder='true'])::-webkit-scrollbar {
            width: 6px;
        }
        .sprint-kanban-root .sprint-kanban-column-body-natural:not([data-drag-placeholder='true'])::-webkit-scrollbar-track {
            background: transparent;
        }
        .sprint-kanban-root .sprint-kanban-column-body-natural:not([data-drag-placeholder='true'])::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 999px;
        }
        .sprint-kanban-root .sprint-kanban-column-body-natural:not([data-drag-placeholder='true']):hover::-webkit-scrollbar-thumb {
            background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.28)'};
        }
        .sprint-kanban-root .sprint-kanban-column-body-natural[data-drag-placeholder='true'] {
            min-height: min(72dvh, 520px) !important;
        }
    }
    /* Desktop: column bodies scroll; show vertical scrollbar when content overflows */
    @media (min-width: 1025px) {
        .sprint-kanban-root .sprint-kanban-desktop-split .sprint-kanban-column-body-natural:not([data-drag-placeholder='true']) {
            flex: 1 1 0% !important;
            min-height: 0 !important;
            overflow-x: hidden;
            overflow-y: auto !important;
            overscroll-behavior: contain;
            scrollbar-width: thin;
            -webkit-overflow-scrolling: touch;
            scrollbar-color: ${({ theme }) =>
        theme.colors.background.default === '#141414'
            ? 'rgba(255,255,255,0.28) rgba(255,255,255,0.06)'
            : '#c4c4c4 #f0f0f0'};
        }
        /* Overlay scrollbar: keep thumb hidden until hover */
        .sprint-kanban-root .sprint-kanban-desktop-split .sprint-kanban-column-body-natural:not([data-drag-placeholder='true']) {
            scrollbar-color: transparent transparent;
        }
        .sprint-kanban-root .sprint-kanban-desktop-split .sprint-kanban-column-body-natural:not([data-drag-placeholder='true']):hover {
            scrollbar-color: ${({ theme }) =>
        theme.colors.background.default === '#141414'
            ? 'rgba(255,255,255,0.28) transparent'
            : 'rgba(0,0,0,0.28) transparent'};
        }
        .sprint-kanban-root .sprint-kanban-desktop-split .sprint-kanban-column-body-natural:not([data-drag-placeholder='true'])::-webkit-scrollbar {
            width: 6px;
        }
        .sprint-kanban-root .sprint-kanban-desktop-split .sprint-kanban-column-body-natural:not([data-drag-placeholder='true'])::-webkit-scrollbar-track {
            background: transparent;
        }
        .sprint-kanban-root .sprint-kanban-desktop-split .sprint-kanban-column-body-natural:not([data-drag-placeholder='true'])::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 999px;
        }
        .sprint-kanban-root .sprint-kanban-desktop-split .sprint-kanban-column-body-natural:not([data-drag-placeholder='true']):hover::-webkit-scrollbar-thumb {
            background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.28)'};
        }
        .sprint-kanban-root .sprint-kanban-desktop-split .sprint-kanban-column-body-natural[data-drag-placeholder='true'] {
            flex: 1 1 0% !important;
            min-height: 0 !important;
            overflow-x: hidden;
            overflow-y: hidden !important;
            overscroll-behavior: contain;
        }
    }
    .sprint-kanban-root .sprint-kanban-column-body-natural[data-drag-placeholder='true'] {
        overflow: hidden !important;
    }
    .sprint-kanban-root .sprint-kanban-flow-hint-trigger {
        margin-left: auto;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        flex-shrink: 1;
        min-width: 0;
        max-width: min(200px, 42vw);
        padding: 3px 8px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 11px;
        font-weight: 600;
        font-family: var(--font-family-geist), system-ui, sans-serif;
        line-height: 1.2;
        transition: background 0.15s ease, color 0.15s ease;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.55)' : '#6b7280')};
        border: 1px solid transparent;
        box-sizing: border-box;
    }
    .sprint-kanban-root .sprint-kanban-flow-hint-trigger:hover {
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.88)' : '#111827')};
        border-color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
    }
    /* Card actions (edit / delete) — always visible. */
    .sprint-kanban-root .sprint-kanban-task-drag [data-kanban-task-card-actions] {
        opacity: 1;
        pointer-events: auto;
    }
    .sprint-kanban-root .sprint-kanban-task-drag [data-kanban-task-card-actions] .ant-btn {
        background: var(--ant-color-fill-tertiary, rgba(0, 0, 0, 0.06)) !important;
        border: 1px solid transparent !important;
        box-shadow: none !important;
        color: var(--ant-color-text-secondary, rgba(0, 0, 0, 0.55)) !important;
    }
    .sprint-kanban-root .sprint-kanban-task-drag [data-kanban-task-card-actions] .ant-btn .anticon {
        color: currentColor !important;
    }
    .sprint-kanban-root .sprint-kanban-task-drag [data-kanban-task-card-actions] .ant-btn:hover {
        border-color: var(--ant-color-border-secondary, rgba(0, 0, 0, 0.12)) !important;
        color: var(--ant-color-text, rgba(0, 0, 0, 0.88)) !important;
    }
    .sprint-kanban-root .sprint-detail-description-html a,
    .sprint-kanban-root .sprint-detail-description-html .mention {
        color: var(--ant-color-primary, #7c3aed);
        font-weight: 500;
        text-decoration: none;
        border-bottom: 1px solid color-mix(in srgb, var(--ant-color-primary, #7c3aed) 45%, transparent);
    }
    .sprint-kanban-root .sprint-detail-description-html a:hover,
    .sprint-kanban-root .sprint-detail-description-html .mention:hover {
        color: color-mix(in srgb, var(--ant-color-primary, #7c3aed) 85%, #000);
        border-bottom-color: color-mix(in srgb, var(--ant-color-primary, #7c3aed) 70%, transparent);
    }
    /* Sprint detail modal: image preview backdrop should look glassy. */
    .ant-image-preview-root .ant-image-preview-mask {
        background: rgba(5, 5, 8, 0.72) !important;
        backdrop-filter: blur(10px) saturate(120%);
        -webkit-backdrop-filter: blur(10px) saturate(120%);
    }
`;

/** Shared by Sprints Kanban and QA Kanban — native drag + float clone. */
export function TaskDragWrap({
    sprintId,
    sourceColumn,
    taskId,
    onTaskDragStart,
    onTaskDragEnd,
    /** Merged into JSON drag payload (e.g. { submissionId } for PMO board). */
    extraDragPayload,
    /** When false, hides the drag handle and disables drag (e.g. PMO submission without submit permission). */
    dragEnabled = true,
    /** QA board: disable the source row collapse animation on drag start. */
    disableSourceShrink = false,
    /** Sprints Todo column: edit/delete (optional). */
    task,
    onTaskEditClick,
    onTaskDeleteClick,
    children,
}) {
    const hideEditDeleteForQaReturn = Boolean(task && isTaskReturnedByQa(task));
    const showCardActionsBar =
        dragEnabled ||
        Boolean(task && (onTaskEditClick || onTaskDeleteClick) && !hideEditDeleteForQaReturn);
    const flatDragPreview = Boolean(extraDragPayload?.flatDragPreview);
    const hideSourceWhileDragging = extraDragPayload?.hideSourceWhileDragging !== false;

    /**
     * Native setDragImage() is rasterized with heavy transparency in Chromium — looks nothing like the card.
     * Use an invisible 1×1 drag image plus a fixed-position clone that follows the drag event (full DOM paint).
     * Drag starts from the grip or from anywhere on the card (HTML5 drag after pointer movement; click still opens detail).
     */
    const wrapperRef = useRef(null);
    const floatRef = useRef(null);
    const capturedHeightRef = useRef(null); // Records card height before drag so ghost placeholder matches
    const dragMoveHandlerRef = useRef(null);
    const dragEndCleanupRef = useRef(null);
    const mountedRef = useRef(true);
    const [shrinkSource, setShrinkSource] = useState(false);
    const [fadeSource, setFadeSource] = useState(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const runDragEnd = useCallback(() => {
        if (dragEndCleanupRef.current) {
            try {
                dragEndCleanupRef.current();
            } catch {
                /* ignore */
            }
            dragEndCleanupRef.current = null;
        }
        if (dragMoveHandlerRef.current) {
            document.removeEventListener('drag', dragMoveHandlerRef.current);
            dragMoveHandlerRef.current = null;
        }
        const floatEl = floatRef.current;
        floatRef.current = null;

        const finishShrink = () => {
            queueMicrotask(() => {
                if (mountedRef.current) {
                    setShrinkSource(false);
                    setFadeSource(false);
                    capturedHeightRef.current = null;
                }
            });
        };

        if (floatEl) {
            const safeRemove = () => {
                try {
                    floatEl.remove();
                } catch {
                    /* ignore */
                }
            };

            // Fallback: ensure the float never "sticks" on screen.
            const fallbackT = window.setTimeout(() => {
                safeRemove();
                finishShrink();
            }, KANBAN_FLOAT_EXIT_MS + 80);

            try {
                const anim = floatEl.animate(
                    [
                        { opacity: 1, transform: 'scale(1)' },
                        { opacity: 0, transform: 'scale(0.97)' },
                    ],
                    {
                        duration: KANBAN_FLOAT_EXIT_MS,
                        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                        fill: 'forwards',
                    },
                );

                // Some browsers can fail to resolve `.finished` reliably; always clear fallback.
                anim.finished
                    .then(() => safeRemove())
                    .catch(() => safeRemove())
                    .finally(() => {
                        window.clearTimeout(fallbackT);
                        finishShrink();
                    });
            } catch {
                window.clearTimeout(fallbackT);
                safeRemove();
                finishShrink();
            }
        } else {
            finishShrink();
        }
        document.body.classList.remove(SPRINT_KANBAN_DRAG_CLASS);
        onTaskDragEnd?.();
    }, [onTaskDragEnd]);

    const runDragStart = useCallback(
        (e) => {
            if (!dragEnabled) {
                e.preventDefault();
                return;
            }
            if (!taskId) {
                e.preventDefault();
                return;
            }
            const el = wrapperRef.current;
            if (!el) {
                e.preventDefault();
                return;
            }

            // Capture height NOW before any state changes (setShrinkSource/setFadeSource) alter layout
            capturedHeightRef.current = el.getBoundingClientRect().height || el.offsetHeight || null;

            /** Grip is inside the wrapper — shrinking the wrapper here aborts native drag in Chromium. */
            const dragFromGrip =
                typeof e.target?.closest === 'function' && Boolean(e.target.closest('[data-kanban-drag-handle]'));

            if (floatRef.current) {
                try {
                    floatRef.current.remove();
                } catch {
                    /* ignore */
                }
                floatRef.current = null;
            }
            if (dragMoveHandlerRef.current) {
                document.removeEventListener('drag', dragMoveHandlerRef.current);
                dragMoveHandlerRef.current = null;
            }
            if (dragEndCleanupRef.current) {
                try {
                    dragEndCleanupRef.current();
                } catch {
                    /* ignore */
                }
                dragEndCleanupRef.current = null;
            }

            const rect = el.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const offsetY = e.clientY - rect.top;

            const selectedIds = extraDragPayload?.selectedTaskIds;
            const stackCountRaw =
                typeof extraDragPayload?.dragStackCount === 'number'
                    ? extraDragPayload.dragStackCount
                    : Array.isArray(selectedIds)
                        ? selectedIds.length
                        : 0;
            const stackCount = Math.max(0, Math.floor(stackCountRaw || 0));

            const floatRoot = document.createElement('div');
            floatRoot.classList.add('sprint-kanban-task-drag-float');
            Object.assign(floatRoot.style, {
                position: 'fixed',
                left: `${rect.left}px`,
                top: `${rect.top}px`,
                width: `${rect.width}px`,
                maxWidth: `${rect.width}px`,
                boxSizing: 'border-box',
                pointerEvents: 'none',
                opacity: '1',
                zIndex: '2147483646',
                margin: '0',
            });

            const makeCloneLayer = (layerEl, styles) => {
                layerEl.classList.add('sprint-kanban-task-drag-float-layer');
                Object.assign(layerEl.style, {
                    position: 'absolute',
                    left: '0px',
                    top: '0px',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    pointerEvents: 'none',
                    margin: '0',
                    borderRadius: '12px',
                    ...styles,
                });
                return layerEl;
            };

            if (stackCount > 1) {
                // Max 3 visible cards in the drag preview.
                const visibleCount = stackCount > 3 ? 3 : stackCount;

                const back2 =
                    visibleCount >= 3
                        ? makeCloneLayer(el.cloneNode(true), {
                            // Pure vertical stack (from top), no horizontal drift, no scaling.
                            transform: 'translate(0px, -16px)',
                            opacity: '0.24',
                            boxShadow: flatDragPreview ? 'none' : '0 10px 30px rgba(0, 0, 0, 0.10)',
                        })
                        : null;
                const back1 =
                    visibleCount >= 2
                        ? makeCloneLayer(el.cloneNode(true), {
                            transform: 'translate(0px, -8px)',
                            opacity: '0.38',
                            boxShadow: flatDragPreview ? 'none' : '0 14px 40px rgba(0, 0, 0, 0.12)',
                        })
                        : null;
                const top = makeCloneLayer(el.cloneNode(true), {
                    transform: 'translate(0px, 0px)',
                    opacity: '1',
                    boxShadow: flatDragPreview ? 'none' : '0 22px 56px rgba(0, 0, 0, 0.18)',
                });

                const badge = document.createElement('div');
                badge.textContent = stackCount > 3 ? '3+' : String(stackCount);
                Object.assign(badge.style, {
                    position: 'absolute',
                    right: '10px',
                    bottom: '10px',
                    minWidth: '22px',
                    height: '22px',
                    padding: '0 7px',
                    borderRadius: '999px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '700',
                    lineHeight: '1',
                    color: '#fff',
                    background: 'var(--ant-color-primary, #7c3aed)',
                    boxShadow: '0 10px 26px rgba(0, 0, 0, 0.18)',
                    zIndex: '5',
                    fontFamily: 'var(--font-family-geist), system-ui, sans-serif',
                    letterSpacing: '0.02em',
                });

                if (back2) floatRoot.appendChild(back2);
                if (back1) floatRoot.appendChild(back1);
                floatRoot.appendChild(top);
                floatRoot.appendChild(badge);
            } else {
                const floatEl = makeCloneLayer(el.cloneNode(true), {
                    transform: 'translate(0px, 0px)',
                    opacity: '1',
                    boxShadow: flatDragPreview ? 'none' : '0 22px 56px rgba(0, 0, 0, 0.18)',
                });
                floatRoot.appendChild(floatEl);
            }

            document.body.appendChild(floatRoot);
            floatRef.current = floatRoot;
            if (hideSourceWhileDragging) {
                // Hide the source card while dragging so it doesn't look like a "stack" for single drag.
                setFadeSource(true);
            }

            // Some flows (opening a modal on drop) can prevent the element's `dragend` from firing.
            // Listen globally as a safety net so the float preview never "sticks" on screen.
            const onGlobalDragFinish = () => {
                runDragEnd();
            };
            window.addEventListener('drop', onGlobalDragFinish, true);
            window.addEventListener('dragend', onGlobalDragFinish, true);
            // Extra fallback: ensure cleanup happens even if browser skips events.
            const globalT = window.setTimeout(() => {
                runDragEnd();
            }, 1200);
            dragEndCleanupRef.current = () => {
                window.removeEventListener('drop', onGlobalDragFinish, true);
                window.removeEventListener('dragend', onGlobalDragFinish, true);
                window.clearTimeout(globalT);
            };

            try {
                e.dataTransfer.setDragImage(getTransparentDragCanvas(), 0, 0);
            } catch {
                /* Safari / edge cases */
            }

            const onDragMove = (ev) => {
                if (!floatRef.current) return;
                if (ev.clientX === 0 && ev.clientY === 0) return;
                floatRef.current.style.left = `${ev.clientX - offsetX}px`;
                floatRef.current.style.top = `${ev.clientY - offsetY}px`;
            };
            dragMoveHandlerRef.current = onDragMove;
            document.addEventListener('drag', onDragMove);

            if (!disableSourceShrink && !dragFromGrip) {
                requestAnimationFrame(() => {
                    setShrinkSource(true);
                });
            }
            onTaskDragStart?.(sourceColumn, taskId, extraDragPayload);
            const payload = { sprintId, taskId, sourceColumn, ...(extraDragPayload && typeof extraDragPayload === 'object' ? extraDragPayload : {}) };
            const payloadStr = JSON.stringify(payload);
            e.dataTransfer.setData('application/json', payloadStr);
            e.dataTransfer.setData('text/plain', payloadStr);
            e.dataTransfer.effectAllowed = 'move';
            document.body.classList.add(SPRINT_KANBAN_DRAG_CLASS);

            // Lock horizontal scroll on all kanban scroll containers in JavaScript.
            // CSS overflow:hidden alone does NOT stop native HTML5 drag auto-scroll.
            const scrollLockEls = document.querySelectorAll(
                '.sprint-kanban-h-scroll-sync, .sprint-kanban-column-row-natural'
            );
            const lockedScrollPositions = [];
            scrollLockEls.forEach((el) => {
                const savedLeft = el.scrollLeft;
                const handler = () => { el.scrollLeft = savedLeft; };
                el.addEventListener('scroll', handler, { passive: true });
                lockedScrollPositions.push({ el, handler });
            });
            // Store cleanup on the drag-end ref alongside existing cleanup
            const prevCleanup = dragEndCleanupRef.current;
            dragEndCleanupRef.current = () => {
                if (prevCleanup) try { prevCleanup(); } catch { /* ignore */ }
                lockedScrollPositions.forEach(({ el, handler }) => {
                    el.removeEventListener('scroll', handler);
                });
            };
        },
        [sprintId, sourceColumn, taskId, extraDragPayload, onTaskDragStart, dragEnabled, disableSourceShrink, runDragEnd],
    );

    return (
        <motion.div
            ref={wrapperRef}
            className="sprint-kanban-task-drag"
            draggable={dragEnabled}
            onDragStart={runDragStart}
            onDragEnd={runDragEnd}
            style={{
                cursor: dragEnabled ? 'grab' : 'default',
                overflow: 'hidden',
                flexShrink: 0,
                position: 'relative',
            }}
            initial={false}
            animate={{
                height: !disableSourceShrink && shrinkSource ? 0 : 'auto',
                opacity: fadeSource ? 0 : 1,
            }}
            transition={{
                height: {
                    duration: KANBAN_SOURCE_SHRINK_DURATION,
                    ease: KANBAN_SOURCE_SHRINK_EASE,
                },
                opacity: {
                    duration: KANBAN_SOURCE_SHRINK_DURATION,
                    ease: [0.4, 0, 0.2, 1],
                },
            }}
        >
            {children}
            {showCardActionsBar ? (
                <span
                    data-kanban-task-card-actions
                    style={{
                        position: 'absolute',
                        bottom: 18,
                        right: 26,
                        zIndex: 4,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 4,
                    }}
                >
                    {task && onTaskEditClick && !hideEditDeleteForQaReturn ? (
                        <Button
                            type="button"
                            $size="13px"
                            $padding="0"
                            aria-label="Edit task"
                            onClick={(e) => {
                                e.stopPropagation();
                                onTaskEditClick(task);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            style={{
                                width: 30,
                                height: 30,
                                minWidth: 30,
                                borderRadius: 9999,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.06))',
                                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                border: '1px solid transparent',
                            }}
                        >
                            <EditOutlined style={{ fontSize: 12 }} />
                        </Button>
                    ) : null}
                    {task && onTaskDeleteClick && !hideEditDeleteForQaReturn ? (
                        <Button
                            type="button"
                            $size="13px"
                            $padding="0"
                            aria-label="Delete task"
                            onClick={(e) => {
                                e.stopPropagation();
                                onTaskDeleteClick(task);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            style={{
                                width: 30,
                                height: 30,
                                minWidth: 30,
                                borderRadius: 9999,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.06))',
                                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                border: '1px solid transparent',
                            }}
                        >
                            <DeleteOutlined style={{ fontSize: 12 }} />
                        </Button>
                    ) : null}
                </span>
            ) : null}
        </motion.div>
    );
}

function DraggableTaskCard({ id, data, children, disabled }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id,
        data,
        disabled,
    });
    const style = transform && !isDragging
        ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
        : undefined;
    return (
        <div
            ref={setNodeRef}
            className="sprint-kanban-task-drag"
            style={{
                ...style,
                opacity: isDragging ? 0 : 1,
                height: isDragging ? 0 : 'auto',
                minHeight: isDragging ? 0 : undefined,
                overflow: isDragging ? 'hidden' : 'visible',
                marginTop: isDragging ? 0 : undefined,
                marginBottom: isDragging ? 0 : undefined,
                paddingTop: isDragging ? 0 : undefined,
                paddingBottom: isDragging ? 0 : undefined,
                cursor: disabled ? 'default' : 'grab',
                flexShrink: 0,
                transition: isDragging ? 'none' : 'opacity 0.12s ease, height 0.12s ease',
            }}
            {...attributes}
            {...listeners}
        >
            {children}
        </div>
    );
}

function DroppableColumn({ id, children }) {
    const { setNodeRef } = useDroppable({ id });
    return (
        <div
            ref={setNodeRef}
            style={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {children}
        </div>
    );
}

function getColumnFromDropId(overId) {
    if (overId === COL.TODO || overId === COL.COMPLETED || overId === COL.SUBMITTED || overId === COL.CONSULTATION) return overId;
    const raw = String(overId ?? '');
    if (!raw) return null;
    const parts = raw.split(':');
    if (parts.length < 3) return null;
    const column = parts[1];
    return column === COL.TODO || column === COL.COMPLETED || column === COL.SUBMITTED || column === COL.CONSULTATION ? column : null;
}

function getTaskIdFromDropId(overId) {
    const raw = String(overId ?? '');
    if (!raw) return null;
    const parts = raw.split(':');
    if (parts.length < 3) return null;
    return parts.slice(2).join(':') || null;
}

function DndTaskCardShell({
    sprintId,
    sourceColumn,
    task,
    onTaskEditClick,
    onTaskDeleteClick,
    /** When true, SprintTaskCard renders ⋯ Edit/Delete; skip absolute-position duplicate buttons */
    hideExternalOverflowActions,
    children,
}) {
    const hideEditDeleteForQaReturn = Boolean(task && isTaskReturnedByQa(task));
    const showCardActionsBar =
        !hideExternalOverflowActions &&
        Boolean(onTaskEditClick || onTaskDeleteClick) &&
        !hideEditDeleteForQaReturn;
    return (
        <div style={{ position: 'relative' }}>
            {children}
            {showCardActionsBar ? (
                <span
                    data-kanban-task-card-actions
                    style={{
                        position: 'absolute',
                        bottom: 18,
                        right: 26,
                        zIndex: 4,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 4,
                        pointerEvents: 'auto',
                    }}
                >
                    {task && onTaskEditClick && !hideEditDeleteForQaReturn ? (
                        <Button
                            type="button"
                            $size="13px"
                            $padding="0"
                            aria-label="Edit task"
                            onClick={(e) => {
                                e.stopPropagation();
                                onTaskEditClick(task);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            style={{
                                width: 30,
                                height: 30,
                                minWidth: 30,
                                borderRadius: 9999,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.06))',
                                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                border: '1px solid transparent',
                            }}
                        >
                            <EditOutlined style={{ fontSize: 12 }} />
                        </Button>
                    ) : null}
                    {task && onTaskDeleteClick && !hideEditDeleteForQaReturn ? (
                        <Button
                            type="button"
                            $size="13px"
                            $padding="0"
                            aria-label="Delete task"
                            onClick={(e) => {
                                e.stopPropagation();
                                onTaskDeleteClick(task);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            style={{
                                width: 30,
                                height: 30,
                                minWidth: 30,
                                borderRadius: 9999,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.06))',
                                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                border: '1px solid transparent',
                            }}
                        >
                            <DeleteOutlined style={{ fontSize: 12 }} />
                        </Button>
                    ) : null}
                </span>
            ) : null}
        </div>
    );
}

function DroppableTaskTarget({ id, children }) {
    const { setNodeRef } = useDroppable({ id });
    return (
        <div ref={setNodeRef} style={{ width: '100%', minWidth: 0 }}>
            {children}
        </div>
    );
}


/**
 * Submit / Move: header dropdown — runs `onFlowAction(sourceColumn, key)` (API bulk) when provided;
 * otherwise scrolls the target column into view.
 * Exported for QA Kanban TODO column (Submit to PMO menu).
 */
export function SprintKanbanColumnFlowHint({ label, menuKeys, sourceColumn, onFlowAction }) {
    const items = menuKeys.map(({ key, label: itemLabel }) => ({ key, label: itemLabel }));
    return (
        <Dropdown
            menu={{
                items,
                onClick: ({ key }) => {
                    if (onFlowAction && sourceColumn) {
                        onFlowAction(sourceColumn, key);
                    } else {
                        scrollSprintKanbanColumnIntoView(key);
                    }
                },
            }}
            trigger={['click']}
            placement="bottomRight"
        >
            <span
                role="button"
                tabIndex={0}
                className="sprint-kanban-flow-hint-trigger"
                aria-label={`${label}, open column menu`}
                aria-haspopup="menu"
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.currentTarget.click();
                    }
                }}
            >
                <span
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </span>
                <DownOutlined style={{ fontSize: 10, flexShrink: 0, opacity: 0.85 }} aria-hidden />
            </span>
        </Dropdown>
    );
}

function KanbanEmptyColumn({ title, token, action }) {
    const border = token?.colorBorderSecondary ?? 'rgba(0,0,0,0.08)';
    const bg = token?.colorFillQuaternary ?? 'rgba(0,0,0,0.02)';
    const text = token?.colorTextSecondary ?? 'rgba(0,0,0,0.55)';
    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                minHeight: '100%',
                borderRadius: 12,
                border: `1px dashed ${border}`,
                background: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                boxSizing: 'border-box',
                color: text,
                textAlign: 'center',
                fontSize: 13,
                lineHeight: 1.4,
                fontFamily: 'var(--font-family-geist), system-ui, sans-serif',
            }}
        >
            <div style={{ maxWidth: 240 }}>
                <div style={{ fontWeight: 700, marginBottom: 6, color: token?.colorText ?? 'rgba(0,0,0,0.88)' }}>
                    No tasks
                </div>
                <div>{title ? `This sprint has no tasks in ${title}.` : 'This sprint has no tasks here yet.'}</div>
                {action ? <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>{action}</div> : null}
            </div>
        </div>
    );
}

function TaskCardStack({
    tasks,
    sprintId,
    sourceColumn,
    kanbanColumn,
    variant,
    onMarkTaskDone,
    onRevertToTodo,
    celebratingTaskKey,
    onCelebrationComplete,
    hideCompletedBadge,
    onTaskOpenDetail,
    onTaskEdit,
    onTaskDelete,
    onTaskAssign,
    /** Embed Edit/Delete in {@link SprintTaskCard} ⋯ menu (Todo + Completed); hide legacy overlay icons */
    useCardEmbeddedOverflowActions = false,
    canCreateTask,
    /** Submitted column: read-only list — no drag handle / long-press (drops from other columns still work). */
    disableTaskDrag = false,
    emptyTitle,
    token,
    onAddTask,
    draggingColumn,
    dragOverTaskId,
    activeDragTaskId,
    isTaskDisabled,
    qaSubmissions = [],
    completedPmoSelectMode = false,
    completedPmoSelectedIds = [],
    onCompletedPmoToggleTask,
}) {
    const [limit, setLimit] = useState(100);

    if (!tasks || tasks.length === 0) {
        const showCreate = emptyTitle === 'Todo' && typeof onAddTask === 'function' && sprintId;
        const action = showCreate ? (
            <Button
                type="button"
                $primary
                $blackOnLight
                $size="13px"
                $padding="6px 12px"
                icon={<PlusOutlined />}
                permissionDenied={!canCreateTask}
                onClick={() => onAddTask(sprintId)}
            >
                Create task
            </Button>
        ) : null;
        return (
            <div style={{ flex: '1 1 0%', minHeight: 0, display: 'flex' }}>
                <KanbanEmptyColumn title={emptyTitle} token={token} action={action} />
            </div>
        );
    }
    const visibleTasks = tasks.slice(0, limit);
    return (
        <>
            {visibleTasks.map((t) => {
                const id = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                const dndId = `${sprintId}:${sourceColumn}:${id}`;
                const isTodoReorderAnchor =
                    sourceColumn === COL.TODO &&
                    draggingColumn === COL.TODO &&
                    dragOverTaskId != null &&
                    String(dragOverTaskId) === String(id) &&
                    activeDragTaskId != null &&
                    String(activeDragTaskId) !== String(id);
                
                const isDisabled = typeof isTaskDisabled === 'function' ? isTaskDisabled(t, sprintId) : false;
                const checked = completedPmoSelectedIds.map(String).includes(String(id));
                const dragEnabled = !completedPmoSelectMode || checked;
                const isPartofActiveDragBatch =
                    completedPmoSelectMode &&
                    activeDragTaskId != null &&
                    completedPmoSelectedIds.map(String).includes(String(activeDragTaskId)) &&
                    completedPmoSelectedIds.map(String).includes(String(id));

                const card = (
                    <SprintTaskCard
                        task={t}
                        qaSubmissions={qaSubmissions}
                        variant={variant}
                        draggable={false}
                        kanbanColumnTintOnly
                        kanbanColumn={kanbanColumn}
                        onMarkDone={onMarkTaskDone}
                        onRevertToTodo={onRevertToTodo}
                        isCelebrating={celebratingTaskKey != null && celebratingTaskKey === `${sprintId}_${id}`}
                        onCelebrationComplete={onCelebrationComplete}
                        hideCompletedBadge={hideCompletedBadge}
                        onOpenDetail={onTaskOpenDetail}
                        onOverflowEdit={useCardEmbeddedOverflowActions ? onTaskEdit : undefined}
                        onOverflowDelete={useCardEmbeddedOverflowActions ? onTaskDelete : undefined}
                        onOverflowAssign={useCardEmbeddedOverflowActions ? onTaskAssign : undefined}
                        suppressOverflowMenu={false}
                        isDisabled={isDisabled}
                    />
                );

                const cardInner = completedPmoSelectMode && sourceColumn === COL.COMPLETED ? (
                    <div
                        style={{
                            width: '100%',
                            flexShrink: 0,
                            minWidth: 0,
                            display: 'flex',
                            gap: 10,
                            alignItems: 'flex-start',
                            height: isPartofActiveDragBatch ? 0 : 'auto',
                            overflow: isPartofActiveDragBatch ? 'hidden' : 'visible',
                            opacity: isPartofActiveDragBatch ? 0 : 1,
                        }}
                    >
                        <Checkbox
                            checked={checked}
                            disabled={isDisabled}
                            onChange={() => onCompletedPmoToggleTask?.(id)}
                            style={{ marginTop: 14, flexShrink: 0 }}
                            aria-label="Select task for PMO submission"
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <DroppableTaskTarget id={dndId}>
                                <DraggableTaskCard
                                    id={dndId}
                                    disabled={!dragEnabled || isDisabled}
                                    isCollapsed={isPartofActiveDragBatch}
                                    data={{
                                        sprintId,
                                        taskId: id,
                                        sourceColumn,
                                        task: t,
                                        selectedTaskIds: checked ? completedPmoSelectedIds : undefined
                                    }}
                                >
                                    <DndTaskCardShell
                                        sprintId={sprintId}
                                        sourceColumn={sourceColumn}
                                        task={t}
                                        hideExternalOverflowActions={useCardEmbeddedOverflowActions && Boolean(onTaskEdit || onTaskDelete || onTaskAssign)}
                                        onTaskEditClick={onTaskEdit}
                                        onTaskDeleteClick={onTaskDelete}
                                    >
                                        {card}
                                    </DndTaskCardShell>
                                </DraggableTaskCard>
                            </DroppableTaskTarget>
                        </div>
                    </div>
                ) : disableTaskDrag ? (
                    card
                ) : (
                    <DroppableTaskTarget id={dndId}>
                        <DraggableTaskCard
                            id={dndId}
                            disabled={isDisabled}
                            data={{ sprintId, taskId: id, sourceColumn, task: t }}
                        >
                            <DndTaskCardShell
                                sprintId={sprintId}
                                sourceColumn={sourceColumn}
                                task={t}
                                hideExternalOverflowActions={useCardEmbeddedOverflowActions && Boolean(onTaskEdit || onTaskDelete || onTaskAssign)}
                                onTaskEditClick={onTaskEdit}
                                onTaskDeleteClick={onTaskDelete}
                            >
                                {card}
                            </DndTaskCardShell>
                        </DraggableTaskCard>
                    </DroppableTaskTarget>
                );

                return (
                    <motion.div
                        key={id}
                        layout
                        transition={{
                            layout: {
                                type: 'spring',
                                stiffness: 150,
                                damping: 24,
                                mass: 1.0,
                            },
                        }}
                        style={{
                            width: '100%',
                            minWidth: 0,
                            flexShrink: 0,
                            marginTop: isTodoReorderAnchor ? 10 : 0,
                            paddingTop: isTodoReorderAnchor ? 6 : 0,
                            borderTop: isTodoReorderAnchor
                                ? '2px dashed var(--ant-color-primary, #7c3aed)'
                                : '2px dashed transparent',
                            borderRadius: 8,
                        }}
                    >
                        {cardInner}
                    </motion.div>
                );
            })}
            {tasks.length > limit && (
                <LoadMoreCard onClick={() => setLimit((l) => l + 100)}>
                    Load More Tasks (+{tasks.length - limit} remaining)
                </LoadMoreCard>
            )}
        </>
    );
}

/**
 * Single-sprint Kanban: Sprint Details, then Tasks (Kanban only in Tasks). Columns: Todo | Mark As Completed | Submitted.
 */
export default function SprintKanbanBoard({
    sprint,
    sprintTitle,
    canEditSprint,
    canDeleteSprint,
    deleteSprintPending,
    onEditSprint,
    onDeleteSprint,
    formatDateFn,
    todoTasks,
    completedTasks,
    consultationTasks = [],
    submittedTasks,
    /** When non-empty, Submitted column uses {@link QASubmissionGroupCard} (same as QA tab TODO submissions). */
    submittedSubmissionGroups = [],
    /** Submitted tasks not matched to any QA submission card (shown after groups). */
    orphanSubmittedTasks = [],
    dropTarget,
    draggingColumn,
    onAddTask,
    onConsultationCreate,
    onMarkTaskDone,
    onRevertToTodo,
    celebratingTaskKey,
    onCelebrationComplete,
    onClearDrop,
    onTaskOpenDetail,
    onTaskEdit,
    onTaskDelete,
    onTaskAssign,
    canCreateTask,
    canEditTask,
    canDeleteTask,
    canAssignTask,
    canMarkTaskTodo,
    /** (sourceColumn, targetColumn) — bulk complete / revert / add to QA submission from column headers */
    onColumnFlowAction,
    onDndDragStart,
    onDndDragOverColumn,
    onDndDragEnd,
    openSprintDetailRequestKey,
    completedPmoSelectMode = false,
    completedPmoSelectedIds = [],
    onCompletedPmoStartSelectMode,
    onCompletedPmoCancelSelectMode,
    onCompletedPmoSelectAll,
    onCompletedPmoUnselectAll,
    onCompletedPmoToggle,
}) {
    const { token } = theme.useToken();
    const sprintId = sprint?.id;
    const [sprintDetailModalOpen, setSprintDetailModalOpen] = useState(false);

    const renderCompletedHeaderRight = () => {
        if (!completedPmoSelectMode) {
            return (
                <StyledLinkButton
                    type="button"
                    onClick={onCompletedPmoStartSelectMode}
                    style={{ marginLeft: 'auto', fontSize: 12, lineHeight: 1.2 }}
                >
                    Select
                </StyledLinkButton>
            );
        }
        const n = completedPmoSelectedIds.length;
        const allSelected = completedTasks.length > 0 && n === completedTasks.length;
        return (
            <div
                style={{
                    marginLeft: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    flexShrink: 0,
                    minWidth: 0,
                }}
            >
                <StyledLinkButton
                    type="button"
                    onClick={() => {
                        if (allSelected) onCompletedPmoUnselectAll?.();
                        else onCompletedPmoSelectAll?.();
                    }}
                    style={{ fontSize: 12, lineHeight: 1.2 }}
                >
                    {allSelected ? 'Deselect All' : 'Select All'}
                </StyledLinkButton>
                <span style={{ color: '#ccc', fontSize: 12 }}>|</span>
                <StyledLinkButton
                    type="button"
                    onClick={onCompletedPmoCancelSelectMode}
                    style={{ fontSize: 12, lineHeight: 1.2 }}
                >
                    Cancel
                </StyledLinkButton>
            </div>
        );
    };
    const [submittedGroupsLimit, setSubmittedGroupsLimit] = useState(100);
    const [descriptionPreviewImage, setDescriptionPreviewImage] = useState('');
    const isNarrow = useMatchMedia('(max-width: 1024px)');
    const { headerScrollRef, bodyScrollRef, onHeaderScroll, onBodyScroll } = useSprintKanbanSyncHorizontalScroll();

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const [activeDrag, setActiveDrag] = useState(null);
    const [dragOverTaskId, setDragOverTaskId] = useState(null);
    const previousOpenRequestRef = useRef(openSprintDetailRequestKey);

    useEffect(() => {
        if (openSprintDetailRequestKey == null) return;
        if (previousOpenRequestRef.current === openSprintDetailRequestKey) return;
        previousOpenRequestRef.current = openSprintDetailRequestKey;
        setSprintDetailModalOpen(true);
    }, [openSprintDetailRequestKey]);

    const handleDragStart = useCallback(
        ({ active }) => {
            const data = active?.data?.current;
            if (!data) return;
            setActiveDrag(data);
            onDndDragStart?.({ sourceColumn: data.sourceColumn });
        },
        [onDndDragStart],
    );

    const handleDragOver = useCallback(
        ({ over }) => {
            const overId = over?.id;
            const col = getColumnFromDropId(overId);
            const overTaskId = getTaskIdFromDropId(overId);
            setDragOverTaskId(col === COL.TODO ? overTaskId : null);
            onDndDragOverColumn?.(col);
        },
        [onDndDragOverColumn],
    );

    const handleDragEnd = useCallback(
        ({ active, over }) => {
            const data = active?.data?.current;
            const overId = over?.id;
            const target = getColumnFromDropId(overId);
            const targetTaskId = getTaskIdFromDropId(overId);
            setActiveDrag(null);
            setDragOverTaskId(null);
            onClearDrop?.();
            if (data && target) {
                onDndDragEnd?.({
                    sprintId: data.sprintId,
                    taskId: data.taskId,
                    sourceColumn: data.sourceColumn,
                    targetColumn: target,
                    targetTaskId,
                });
            } else {
                onDndDragEnd?.({
                    sprintId: data?.sprintId,
                    taskId: data?.taskId,
                    sourceColumn: data?.sourceColumn,
                    targetColumn: null,
                    targetTaskId: null,
                });
            }
        },
        [onDndDragEnd, onClearDrop],
    );

    /** State-driven so placeholders appear on the same frame as drag start (ref alone does not re-render). */
    const showDragPlaceholder = (columnId) => Boolean(draggingColumn && draggingColumn !== columnId);

    const dropZoneActive = (columnId) =>
        Boolean(draggingColumn && draggingColumn !== columnId && dropTarget === columnId);

    const submittedTasksStackProps = {
        sprintId,
        sourceColumn: COL.SUBMITTED,
        kanbanColumn: COL.SUBMITTED,
        variant: 'readOnly',
        emptyTitle: 'Submitted',
        token,
        canCreateTask,
        hideCompletedBadge: true,
        disableTaskDrag: true,
        onTaskOpenDetail,
        draggingColumn,
        dragOverTaskId,
        activeDragTaskId: activeDrag?.taskId,
    };

    const renderConsultationColumnContent = () => {
        if (!consultationTasks || consultationTasks.length === 0) {
            return (
                <TaskCardStack
                    tasks={[]}
                    sprintId={sprintId}
                    sourceColumn={COL.CONSULTATION}
                    kanbanColumn={COL.CONSULTATION}
                    variant="editable"
                    emptyTitle="Consultation"
                    token={token}
                    canCreateTask={canCreateTask}
                    draggingColumn={draggingColumn}
                    dragOverTaskId={dragOverTaskId}
                    activeDragTaskId={activeDrag?.taskId}
                    qaSubmissions={sprint?.qaSubmissions || []}
                />
            );
        }

        // Group tasks by consultation.id (each unique consultation becomes one card)
        const consultationGroupMap = new Map();
        const orphanTasks = [];
        for (const task of consultationTasks) {
            const c = task?.consultation;
            if (c && typeof c === 'object' && c.id) {
                if (!consultationGroupMap.has(c.id)) {
                    consultationGroupMap.set(c.id, { consultation: c, tasks: [] });
                }
                consultationGroupMap.get(c.id).tasks.push(task);
            } else {
                orphanTasks.push(task);
            }
        }
        const groups = Array.from(consultationGroupMap.values());

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                {groups.map((group, gi) => {
                    const { consultation, tasks } = group;
                    const descMarkdown =
                        typeof consultation.description?.markdown === 'string'
                            ? consultation.description.markdown
                            : typeof consultation.description === 'string'
                                ? consultation.description
                                : '';
                    const attachmentCount = Array.isArray(consultation.attachments)
                        ? consultation.attachments.length
                        : 0;
                    return (
                        <QASubmissionGroupCard
                            key={consultation.id ?? `consult-${gi}`}
                            sidLabel={`CONS-${gi + 1}`}
                            submissionTitle="Consultation"
                            descriptionMarkdown={descMarkdown}
                            attachmentCount={attachmentCount}
                            qaPassed={consultation.qaPassed === true}
                            tasks={tasks}
                            onTaskClick={(task) => onTaskOpenDetail?.(task)}
                            decorateTaskRow={(task, idx, rowInner) => {
                                const id = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
                                const dndId = `${sprintId}:${COL.CONSULTATION}:${id}`;
                                return (
                                    <DroppableTaskTarget id={dndId}>
                                        <DraggableTaskCard
                                            id={dndId}
                                            disabled={false}
                                            data={{ sprintId, taskId: id, sourceColumn: COL.CONSULTATION, task }}
                                        >
                                            <div style={{ cursor: 'grab' }}>
                                                {rowInner}
                                            </div>
                                        </DraggableTaskCard>
                                    </DroppableTaskTarget>
                                );
                            }}
                        />
                    );
                })}
                {/* Orphan tasks (no consultation object) — plain cards */}
                {orphanTasks.map((task, idx) => (
                    <TaskCardStack
                        key={task?.id ?? task?.taskId ?? `orphan-${idx}`}
                        tasks={[task]}
                        sprintId={sprintId}
                        sourceColumn={COL.CONSULTATION}
                        kanbanColumn={COL.CONSULTATION}
                        variant="editable"
                        token={token}
                        canCreateTask={canCreateTask}
                        onMarkTaskDone={canMarkTaskTodo ? onMarkTaskDone : undefined}
                        onRevertToTodo={canMarkTaskTodo ? onRevertToTodo : undefined}
                        celebratingTaskKey={celebratingTaskKey}
                        onCelebrationComplete={onCelebrationComplete}
                        onTaskOpenDetail={onTaskOpenDetail}
                        onTaskEdit={canEditTask ? onTaskEdit : undefined}
                        onTaskDelete={canDeleteTask ? onTaskDelete : undefined}
                        onTaskAssign={canAssignTask ? onTaskAssign : undefined}
                        useCardEmbeddedOverflowActions
                        draggingColumn={draggingColumn}
                        dragOverTaskId={dragOverTaskId}
                        activeDragTaskId={activeDrag?.taskId}
                        qaSubmissions={sprint?.qaSubmissions || []}
                    />
                ))}
            </div>
        );
    };

    const renderSubmittedColumnContent = () => {
        const groups = submittedSubmissionGroups ?? [];
        const orphans = orphanSubmittedTasks ?? [];

        if (groups.length === 0) {
            if (!submittedTasks || submittedTasks.length === 0) {
                return <TaskCardStack tasks={[]} {...submittedTasksStackProps} />;
            }
            return (
                <QASubmissionGroupCard
                    key="virtual-submitted-tasks"
                    sidLabel="SUB"
                    submissionTitle="Submitted Tasks"
                    submissionStatus="submitted_to_qa"
                    descriptionMarkdown="These tasks have been submitted for QA review."
                    attachmentCount={0}
                    tasks={submittedTasks}
                    onTaskClick={(task) => onTaskOpenDetail?.(task)}
                />
            );
        }
        const visibleGroups = groups.slice(0, submittedGroupsLimit);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                {visibleGroups.map((group, gi) => (
                    <QASubmissionGroupCard
                        key={group.id ?? `sprint-submission-${gi}`}
                        sidLabel={group.sidLabel}
                        submissionTitle={group.submissionTitle}
                        submissionStatus={group.submissionStatus}
                        descriptionMarkdown={group.descriptionMarkdown ?? ''}
                        descriptionMentions={group.descriptionMentions}
                        attachmentCount={group.attachmentCount ?? 0}
                        submittedAtLabel={group.submittedAtLabel}
                        submittedBy={group.submittedBy}
                        tasks={group.tasks}
                        onTaskClick={(task) => onTaskOpenDetail?.(task)}
                    />
                ))}
                {groups.length > submittedGroupsLimit && (
                    <LoadMoreCard onClick={() => setSubmittedGroupsLimit(l => l + 100)}>
                        Load More Submissions (+{groups.length - submittedGroupsLimit} remaining)
                    </LoadMoreCard>
                )}
                {orphans.length > 0 && (
                    <QASubmissionGroupCard
                        key="virtual-orphans"
                        sidLabel="SUB-ORPHAN"
                        submissionTitle="Other Submissions"
                        submissionStatus="submitted_to_qa"
                        descriptionMarkdown="These tasks have been submitted for QA review."
                        attachmentCount={0}
                        tasks={orphans}
                        onTaskClick={(task) => onTaskOpenDetail?.(task)}
                    />
                )}
            </div>
        );
    };

    const kanbanColumns = isNarrow ? (
        <KanbanThreeColumnRow
            className="sprint-kanban-column-row-natural"
            style={{ marginTop: 0 }}
        >
            <KanbanColumnPanel data-kanban-drop-column={COL.TODO}>
                <KanbanColumnHeader style={{ width: '100%' }}>
                    <KanbanColumnTitle>Todo</KanbanColumnTitle>
                    <KanbanColumnCountBadge $variant={COL.TODO}>{todoTasks.length}</KanbanColumnCountBadge>
                    <span style={{ flex: 1 }} />
                    {onAddTask ? (
                        <Button
                            type="button"
                            $primary
                            $blackOnLight
                            $size="11px"
                            $padding="0 10px"
                            icon={<PlusOutlined />}
                            permissionDenied={!canCreateTask}
                            onClick={() => onAddTask(sprintId)}
                            style={{ height: 22, minHeight: 22, borderRadius: 6, marginRight: 0 }}
                        >
                            Create
                        </Button>
                    ) : null}
                </KanbanColumnHeader>
                <DroppableColumn id={COL.TODO}>
                    <KanbanColumnBody
                        className="sprint-kanban-column-body-natural"
                        data-drag-placeholder={showDragPlaceholder(COL.TODO) ? 'true' : undefined}
                        $dragPlaceholder={showDragPlaceholder(COL.TODO)}
                    >
                        {showDragPlaceholder(COL.TODO) ? (
                            <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.TODO)}>
                                <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                <span>{COLUMN_DROP_MESSAGES[COL.TODO]}</span>
                            </KanbanDragDropPlaceholder>
                        ) : (
                            <TaskCardStack
                                tasks={todoTasks}
                                sprintId={sprintId}
                                sourceColumn={COL.TODO}
                                kanbanColumn={COL.TODO}
                                variant="editable"
                                emptyTitle="Todo"
                                token={token}
                                onAddTask={onAddTask}
                                canCreateTask={canCreateTask}
                                onMarkTaskDone={canMarkTaskTodo ? onMarkTaskDone : undefined}
                                onRevertToTodo={canMarkTaskTodo ? onRevertToTodo : undefined}
                                celebratingTaskKey={celebratingTaskKey}
                                onCelebrationComplete={onCelebrationComplete}
                                onTaskOpenDetail={onTaskOpenDetail}
                                onTaskEdit={canEditTask ? onTaskEdit : undefined}
                                onTaskDelete={canDeleteTask ? onTaskDelete : undefined}
                                onTaskAssign={canAssignTask ? onTaskAssign : undefined}
                                useCardEmbeddedOverflowActions
                                draggingColumn={draggingColumn}
                                dragOverTaskId={dragOverTaskId}
                                activeDragTaskId={activeDrag?.taskId}
                                qaSubmissions={sprint?.qaSubmissions || []}
                            />
                        )}
                    </KanbanColumnBody>
                </DroppableColumn>
            </KanbanColumnPanel>

            <KanbanColumnPanel data-kanban-drop-column={COL.COMPLETED}>
                <KanbanColumnHeader style={{ width: '100%' }}>
                    <KanbanColumnTitle>Mark As Completed</KanbanColumnTitle>
                    <TaskCardKanbanStatusPillCount as="span" aria-label={`${completedTasks.length} tasks`}>
                        {completedTasks.length}
                    </TaskCardKanbanStatusPillCount>
                    {renderCompletedHeaderRight()}
                </KanbanColumnHeader>
                <DroppableColumn id={COL.COMPLETED}>
                    <KanbanColumnBody
                        className="sprint-kanban-column-body-natural"
                        data-drag-placeholder={showDragPlaceholder(COL.COMPLETED) ? 'true' : undefined}
                        $dragPlaceholder={showDragPlaceholder(COL.COMPLETED)}
                    >
                        {showDragPlaceholder(COL.COMPLETED) ? (
                            <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.COMPLETED)}>
                                <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                <span>{COLUMN_DROP_MESSAGES[COL.COMPLETED]}</span>
                            </KanbanDragDropPlaceholder>
                        ) : (
                            <TaskCardStack
                                tasks={completedTasks}
                                sprintId={sprintId}
                                sourceColumn={COL.COMPLETED}
                                kanbanColumn={COL.COMPLETED}
                                variant="editable"
                                emptyTitle="Completed"
                                token={token}
                                canCreateTask={canCreateTask}
                                onMarkTaskDone={canMarkTaskTodo ? onMarkTaskDone : undefined}
                                onRevertToTodo={canMarkTaskTodo ? onRevertToTodo : undefined}
                                celebratingTaskKey={celebratingTaskKey}
                                onCelebrationComplete={onCelebrationComplete}
                                onTaskOpenDetail={onTaskOpenDetail}
                                onTaskEdit={canEditTask ? onTaskEdit : undefined}
                                onTaskDelete={canDeleteTask ? onTaskDelete : undefined}
                                onTaskAssign={canAssignTask ? onTaskAssign : undefined}
                                useCardEmbeddedOverflowActions
                                draggingColumn={draggingColumn}
                                dragOverTaskId={dragOverTaskId}
                                activeDragTaskId={activeDrag?.taskId}
                                qaSubmissions={sprint?.qaSubmissions || []}
                                completedPmoSelectMode={completedPmoSelectMode}
                                completedPmoSelectedIds={completedPmoSelectedIds}
                                onCompletedPmoToggleTask={onCompletedPmoToggle}
                            />
                        )}
                    </KanbanColumnBody>
                </DroppableColumn>
            </KanbanColumnPanel>

            <KanbanColumnPanel data-kanban-drop-column={COL.CONSULTATION}>
                <KanbanColumnHeader>
                    <KanbanColumnTitle>Consultation</KanbanColumnTitle>
                    <KanbanColumnCountBadge $variant={COL.CONSULTATION}>{consultationTasks.length}</KanbanColumnCountBadge>
                    <SprintKanbanColumnFlowHint
                        {...COLUMN_HEADER_FLOW[COL.CONSULTATION]}
                        sourceColumn={COL.CONSULTATION}
                        onFlowAction={onColumnFlowAction}
                    />
                </KanbanColumnHeader>
                <DroppableColumn id={COL.CONSULTATION}>
                    <KanbanColumnBody
                        className="sprint-kanban-column-body-natural"
                        data-drag-placeholder={showDragPlaceholder(COL.CONSULTATION) ? 'true' : undefined}
                        $dragPlaceholder={showDragPlaceholder(COL.CONSULTATION)}
                    >
                        {showDragPlaceholder(COL.CONSULTATION) ? (
                            <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.CONSULTATION)}>
                                <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                <span>{COLUMN_DROP_MESSAGES[COL.CONSULTATION]}</span>
                            </KanbanDragDropPlaceholder>
                        ) : (
                            renderConsultationColumnContent()
                        )}
                    </KanbanColumnBody>
                </DroppableColumn>
            </KanbanColumnPanel>

            <KanbanColumnPanel data-kanban-drop-column={COL.SUBMITTED}>
                <KanbanColumnHeader>
                    <KanbanColumnTitle>Submitted</KanbanColumnTitle>
                    <KanbanColumnCountBadge $variant={COL.SUBMITTED}>{submittedTasks.length}</KanbanColumnCountBadge>
                </KanbanColumnHeader>
                <DroppableColumn id={COL.SUBMITTED}>
                    <KanbanColumnBody
                        className="sprint-kanban-column-body-natural"
                        data-drag-placeholder={showDragPlaceholder(COL.SUBMITTED) ? 'true' : undefined}
                        $dragPlaceholder={showDragPlaceholder(COL.SUBMITTED)}
                    >
                        {showDragPlaceholder(COL.SUBMITTED) ? (
                            <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.SUBMITTED)}>
                                <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                <span>{COLUMN_DROP_MESSAGES[COL.SUBMITTED]}</span>
                            </KanbanDragDropPlaceholder>
                        ) : (
                            renderSubmittedColumnContent()
                        )}
                    </KanbanColumnBody>
                </DroppableColumn>
            </KanbanColumnPanel>
        </KanbanThreeColumnRow>
    ) : (
        <div className="sprint-kanban-desktop-split">
            <div data-sprint-kanban-sticky-strip className="sprint-kanban-sticky-strip">
                <div ref={headerScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onHeaderScroll}>
                    <div style={kanbanDesktopBoardHeaderRowInnerStyle}>
                        <div style={kanbanDesktopHeaderCellFlexStyle} data-kanban-drop-column={COL.TODO}>
                            <KanbanColumnHeader style={{ width: '100%' }}>
                                <KanbanColumnTitle>Todo</KanbanColumnTitle>
                                <KanbanColumnCountBadge $variant={COL.TODO}>{todoTasks.length}</KanbanColumnCountBadge>
                                <span style={{ flex: 1 }} />
                                {onAddTask ? (
                                    <Button
                                        type="button"
                                        $primary
                                        $blackOnLight
                                        $size="11px"
                                        $padding="0 10px"
                                        icon={<PlusOutlined />}
                                        permissionDenied={!canCreateTask}
                                        onClick={() => onAddTask(sprintId)}
                                        style={{ height: 22, minHeight: 22, borderRadius: 6, marginRight: 8 }}
                                    >
                                        Create
                                    </Button>
                                ) : null}
                            </KanbanColumnHeader>
                        </div>
                        <div style={kanbanDesktopHeaderCellFlexStyle} data-kanban-drop-column={COL.COMPLETED}>
                            <KanbanColumnHeader style={{ width: '100%' }}>
                                <KanbanColumnTitle>Mark As Completed</KanbanColumnTitle>
                                <TaskCardKanbanStatusPillCount as="span" aria-label={`${completedTasks.length} tasks`}>
                                    {completedTasks.length}
                                </TaskCardKanbanStatusPillCount>
                                {renderCompletedHeaderRight()}
                            </KanbanColumnHeader>
                        </div>
                        <div style={kanbanDesktopHeaderCellFlexStyle} data-kanban-drop-column={COL.CONSULTATION}>
                            <KanbanColumnHeader style={{ width: '100%' }}>
                                <KanbanColumnTitle>Consultation</KanbanColumnTitle>
                                <TaskCardKanbanStatusPillCount as="span" aria-label={`${consultationTasks.length} tasks`}>
                                    {consultationTasks.length}
                                </TaskCardKanbanStatusPillCount>
                                <span style={{ flex: 1 }} />
                                {onConsultationCreate ? (
                                    <Button
                                        type="button"
                                        $primary
                                        $blackOnLight
                                        $size="11px"
                                        $padding="0 10px"
                                        icon={<PlusOutlined />}
                                        onClick={() => onConsultationCreate()}
                                        style={{ height: 22, minHeight: 22, borderRadius: 6, marginRight: 8 }}
                                    >
                                        Create
                                    </Button>
                                ) : null}
                                <SprintKanbanColumnFlowHint
                                    {...COLUMN_HEADER_FLOW[COL.CONSULTATION]}
                                    sourceColumn={COL.CONSULTATION}
                                    onFlowAction={onColumnFlowAction}
                                />
                            </KanbanColumnHeader>
                        </div>
                        <div style={kanbanDesktopHeaderCellFlexStyle} data-kanban-drop-column={COL.SUBMITTED}>
                            <KanbanColumnHeader>
                                <KanbanColumnTitle>Submitted</KanbanColumnTitle>
                                <KanbanColumnCountBadge $variant={COL.SUBMITTED}>{submittedTasks.length}</KanbanColumnCountBadge>
                            </KanbanColumnHeader>
                        </div>
                    </div>
                </div>
            </div>
            <div className="sprint-kanban-desktop-body-wrap">
                <div ref={bodyScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onBodyScroll}>
                    <div
                        className="sprint-kanban-desktop-board-row"
                        style={kanbanDesktopBoardRowInnerStyle}
                    >
                        <KanbanColumnPanel data-kanban-drop-column={COL.TODO}>
                            <DroppableColumn id={COL.TODO}>
                                <KanbanColumnBody
                                    className="sprint-kanban-column-body-natural"
                                    data-drag-placeholder={showDragPlaceholder(COL.TODO) ? 'true' : undefined}
                                    $dragPlaceholder={showDragPlaceholder(COL.TODO)}
                                >
                                    {showDragPlaceholder(COL.TODO) ? (
                                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.TODO)}>
                                            <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                            <span>{COLUMN_DROP_MESSAGES[COL.TODO]}</span>
                                        </KanbanDragDropPlaceholder>
                                    ) : (
                                        <TaskCardStack
                                            tasks={todoTasks}
                                            sprintId={sprintId}
                                            sourceColumn={COL.TODO}
                                            kanbanColumn={COL.TODO}
                                            variant="editable"
                                            emptyTitle="Todo"
                                            token={token}
                                            onAddTask={onAddTask}
                                            canCreateTask={canCreateTask}
                                            onMarkTaskDone={canMarkTaskTodo ? onMarkTaskDone : undefined}
                                            onRevertToTodo={canMarkTaskTodo ? onRevertToTodo : undefined}
                                            celebratingTaskKey={celebratingTaskKey}
                                            onCelebrationComplete={onCelebrationComplete}
                                            onTaskOpenDetail={onTaskOpenDetail}
                                            onTaskEdit={canEditTask ? onTaskEdit : undefined}
                                            onTaskDelete={canDeleteTask ? onTaskDelete : undefined}
                                            onTaskAssign={canAssignTask ? onTaskAssign : undefined}
                                            useCardEmbeddedOverflowActions
                                            draggingColumn={draggingColumn}
                                            dragOverTaskId={dragOverTaskId}
                                            activeDragTaskId={activeDrag?.taskId}
                                            qaSubmissions={sprint?.qaSubmissions || []}
                                        />
                                    )}
                                </KanbanColumnBody>
                            </DroppableColumn>
                        </KanbanColumnPanel>

                        <KanbanColumnPanel data-kanban-drop-column={COL.COMPLETED}>
                            <DroppableColumn id={COL.COMPLETED}>
                                <KanbanColumnBody
                                    className="sprint-kanban-column-body-natural"
                                    data-drag-placeholder={showDragPlaceholder(COL.COMPLETED) ? 'true' : undefined}
                                    $dragPlaceholder={showDragPlaceholder(COL.COMPLETED)}
                                >
                                    {showDragPlaceholder(COL.COMPLETED) ? (
                                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.COMPLETED)}>
                                            <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                            <span>{COLUMN_DROP_MESSAGES[COL.COMPLETED]}</span>
                                        </KanbanDragDropPlaceholder>
                                    ) : (
                                        <TaskCardStack
                                            tasks={completedTasks}
                                            sprintId={sprintId}
                                            sourceColumn={COL.COMPLETED}
                                            kanbanColumn={COL.COMPLETED}
                                            variant="editable"
                                            emptyTitle="Completed"
                                            token={token}
                                            canCreateTask={canCreateTask}
                                            onMarkTaskDone={canMarkTaskTodo ? onMarkTaskDone : undefined}
                                            onRevertToTodo={canMarkTaskTodo ? onRevertToTodo : undefined}
                                            celebratingTaskKey={celebratingTaskKey}
                                            onCelebrationComplete={onCelebrationComplete}
                                            onTaskOpenDetail={onTaskOpenDetail}
                                            onTaskEdit={canEditTask ? onTaskEdit : undefined}
                                            onTaskDelete={canDeleteTask ? onTaskDelete : undefined}
                                            onTaskAssign={canAssignTask ? onTaskAssign : undefined}
                                            useCardEmbeddedOverflowActions
                                            draggingColumn={draggingColumn}
                                            dragOverTaskId={dragOverTaskId}
                                            activeDragTaskId={activeDrag?.taskId}
                                            qaSubmissions={sprint?.qaSubmissions || []}
                                            completedPmoSelectMode={completedPmoSelectMode}
                                            completedPmoSelectedIds={completedPmoSelectedIds}
                                            onCompletedPmoToggleTask={onCompletedPmoToggle}
                                        />
                                    )}
                                </KanbanColumnBody>
                            </DroppableColumn>
                        </KanbanColumnPanel>

                        <KanbanColumnPanel data-kanban-drop-column={COL.CONSULTATION}>
                            <DroppableColumn id={COL.CONSULTATION}>
                                <KanbanColumnBody
                                    className="sprint-kanban-column-body-natural"
                                    data-drag-placeholder={showDragPlaceholder(COL.CONSULTATION) ? 'true' : undefined}
                                    $dragPlaceholder={showDragPlaceholder(COL.CONSULTATION)}
                                >
                                    {showDragPlaceholder(COL.CONSULTATION) ? (
                                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.CONSULTATION)}>
                                            <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                            <span>{COLUMN_DROP_MESSAGES[COL.CONSULTATION]}</span>
                                        </KanbanDragDropPlaceholder>
                                    ) : (
                                        renderConsultationColumnContent()
                                    )}
                                </KanbanColumnBody>
                            </DroppableColumn>
                        </KanbanColumnPanel>

                        <KanbanColumnPanel data-kanban-drop-column={COL.SUBMITTED}>
                            <DroppableColumn id={COL.SUBMITTED}>
                                <KanbanColumnBody
                                    className="sprint-kanban-column-body-natural"
                                    data-drag-placeholder={showDragPlaceholder(COL.SUBMITTED) ? 'true' : undefined}
                                    $dragPlaceholder={showDragPlaceholder(COL.SUBMITTED)}
                                >
                                    {showDragPlaceholder(COL.SUBMITTED) ? (
                                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.SUBMITTED)}>
                                            <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                            <span>{COLUMN_DROP_MESSAGES[COL.SUBMITTED]}</span>
                                        </KanbanDragDropPlaceholder>
                                    ) : (
                                        renderSubmittedColumnContent()
                                    )}
                                </KanbanColumnBody>
                            </DroppableColumn>
                        </KanbanColumnPanel>
                    </div>
                </div>
            </div>
        </div>
    );

    const kanbanRow = (
        <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            autoScroll={false}
        >
            <SprintKanbanFill className="sprint-kanban-fill-natural">{kanbanColumns}</SprintKanbanFill>
            <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
                {activeDrag?.task ? (
                    (() => {
                        const selectedIds = activeDrag.selectedTaskIds;
                        const isStack = Array.isArray(selectedIds) && selectedIds.length > 1;
                        const stackCount = isStack ? selectedIds.length : 1;

                        if (isStack) {
                            return (
                                <div style={{
                                    position: 'relative',
                                    width: 360,
                                    maxWidth: 420,
                                    pointerEvents: 'none',
                                }}>
                                    {/* Bottom Card Stack Shadow */}
                                    <div style={{
                                        position: 'absolute',
                                        inset: '0 4px',
                                        background: 'var(--ant-color-bg-container, #ffffff)',
                                        borderRadius: '12px',
                                        border: '1px solid var(--ant-color-border-secondary, rgba(0, 0, 0, 0.06))',
                                        transform: 'translateY(12px) scale(0.95)',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                        zIndex: 1,
                                    }} />
                                    {/* Middle Card Stack Shadow */}
                                    <div style={{
                                        position: 'absolute',
                                        inset: '0 2px',
                                        background: 'var(--ant-color-bg-container, #ffffff)',
                                        borderRadius: '12px',
                                        border: '1px solid var(--ant-color-border-secondary, rgba(0, 0, 0, 0.06))',
                                        transform: 'translateY(6px) scale(0.975)',
                                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.08)',
                                        zIndex: 2,
                                    }} />
                                    {/* Top Card */}
                                    <div style={{
                                        position: 'relative',
                                        zIndex: 3,
                                        transform: 'scale(1.02)',
                                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.15)',
                                    }}>
                                        <SprintTaskCard
                                            task={activeDrag.task}
                                            variant="readOnly"
                                            draggable={false}
                                            kanbanColumnTintOnly
                                            kanbanColumn={activeDrag.sourceColumn}
                                            suppressOverflowMenu
                                        />
                                        {/* Stack count badge */}
                                        <div style={{
                                            position: 'absolute',
                                            right: '12px',
                                            bottom: '12px',
                                            minWidth: '22px',
                                            height: '22px',
                                            padding: '0 6px',
                                            borderRadius: '999px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            color: '#fff',
                                            background: 'var(--ant-color-primary, #7c3aed)',
                                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                                            zIndex: 5,
                                        }}>
                                            {stackCount}
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        // Single Card
                        return (
                            <div style={{
                                width: 360,
                                maxWidth: 420,
                                opacity: 1,
                                transform: 'scale(1.02)',
                                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.15)',
                                pointerEvents: 'none',
                            }}>
                                <SprintTaskCard
                                    task={activeDrag.task}
                                    variant="readOnly"
                                    draggable={false}
                                    kanbanColumnTintOnly
                                    kanbanColumn={activeDrag.sourceColumn}
                                    suppressOverflowMenu
                                />
                            </div>
                        );
                    })()
                ) : null}
            </DragOverlay>
        </DndContext>
    );

    return (
        <Container
            className="sprint-kanban-root"
            $display="flex"
            $flexDirection="column"
            $flex="1 1 0%"
            $minHeight="0"
            $gap="0"
            $overflow="hidden"
        >
            <SprintKanbanDragCursorStyle />
            <SprintKanbanScrollChainStyle />
            <Container $display="flex" $flexDirection="column" $flex="1 1 0%" $minHeight="0" $overflow="hidden">
                <Container
                    $display="flex"
                    $alignItems="center"
                    $justifyContent="flex-end"
                    $gap="8px"
                    $flex="0 0 auto"
                    style={{ padding: '0 0 0 10px', paddingLeft: 10, paddingInlineStart: 10, minWidth: 0 }}
                >
                    <Container
                        $display="inline-flex"
                        $alignItems="center"
                        $gap="10px"
                        style={{ flexShrink: 0, marginLeft: 'auto', marginRight: 24 }}
                    >
                        <MultipleButton
                            leftLabel="Edit"
                            rightLabel="Delete"
                            leftIcon={<EditOutlined />}
                            rightIcon={<DeleteOutlined />}
                            onLeftClick={canEditSprint ? () => onEditSprint?.() : undefined}
                            onRightClick={canDeleteSprint && !deleteSprintPending ? () => onDeleteSprint?.() : undefined}
                            leftPermissionDenied={!canEditSprint}
                            rightPermissionDenied={!canDeleteSprint}
                            $size="12px"
                            $padding="0 12px"
                            $minHeight="28px"
                            $entryAnimation={false}
                            $autoWidth
                            style={{ height: 28, minHeight: 28, borderRadius: 8 }}
                        />
                    </Container>
                </Container>

                <Container
                    $display="flex"
                    $flex="1 1 0%"
                    $minHeight="0"
                    $overflow="hidden"
                    style={{ paddingLeft: 10, paddingInlineStart: 10, paddingTop: 0 }}
                >
                    {kanbanRow}
                </Container>
            </Container>
            <AppModal
                title={(
                    <Container
                        $display="flex"
                        $alignItems="center"
                        $justifyContent="space-between"
                        $gap="10px"
                        style={{ width: '100%', minWidth: 0, paddingRight: 8 }}
                    >
                        <span style={{ fontWeight: 600, minWidth: 0 }}>Sprint Detail</span>
                        <Container
                            $display="inline-flex"
                            $alignItems="center"
                            $gap="10px"
                            style={{ flexShrink: 0 }}
                        >
                            <MultipleButton
                                leftLabel="Edit"
                                rightLabel="Delete"
                                leftIcon={<EditOutlined />}
                                rightIcon={<DeleteOutlined />}
                                onLeftClick={canEditSprint ? () => onEditSprint?.() : undefined}
                                onRightClick={canDeleteSprint && !deleteSprintPending ? () => onDeleteSprint?.() : undefined}
                                leftPermissionDenied={!canEditSprint}
                                rightPermissionDenied={!canDeleteSprint}
                                $size="12px"
                                $padding="0 12px"
                                $minHeight="28px"
                                $entryAnimation={false}
                                style={{ height: 28, minHeight: 28, borderRadius: 8 }}
                            />
                        </Container>
                    </Container>
                )}
                open={sprintDetailModalOpen}
                onCancel={() => setSprintDetailModalOpen(false)}
                footer={null}
                size="md"
            >
                <Container
                    $display="flex"
                    $flexDirection="column"
                    $gap="8px"
                    $padding="10px 6px 6px"
                    style={{
                        minHeight: 0,
                    }}
                >
                    {(() => {
                        const sprintDescriptionText = getSprintDescriptionText(sprint);
                        const sprintAttachments = getSprintAttachmentList(sprint);
                        const hasSprintDescription = sprintDescriptionText.length > 0;
                        const hasSprintAttachments = sprintAttachments.length > 0;
                        const sprintLinks = sprintAttachments.filter((x) => String(x?.type || '').toLowerCase() === 'link');
                        const sprintFiles = sprintAttachments.filter((x) => String(x?.type || '').toLowerCase() !== 'link');
                        const deadlineText = formatSprintDeadlineRange(sprint, formatDateFn);
                        const progressPercent = getSprintProgressPercent(sprint);
                        const sprintNumberValue = sprint?.sprintNumber ?? sprint?.sprint_number ?? sprint?.number ?? '—';
                        const sprintNameValue = sprint?.name ?? sprint?.title ?? sprintTitle ?? getSprintDisplayLabel(sprint) ?? '';
                        const sectionLabelStyle = {
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 0,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            opacity: 0.75,
                        };
                        return (
                            <Form layout="vertical" requiredMark={false}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <Form.Item label={<PanelSectionLabel style={sectionLabelStyle}>Sprint #</PanelSectionLabel>} style={{ marginBottom: 6 }}>
                                        <Input value={String(sprintNumberValue)} size="large" readOnly style={{ cursor: 'default' }} />
                                    </Form.Item>
                                    <Form.Item label={<PanelSectionLabel style={sectionLabelStyle}>Name</PanelSectionLabel>} style={{ marginBottom: 6 }}>
                                        <Input value={sprintNameValue} size="large" readOnly style={{ cursor: 'default' }} />
                                    </Form.Item>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <Form.Item label={<PanelSectionLabel style={sectionLabelStyle}>Start date</PanelSectionLabel>} style={{ marginBottom: 6 }}>
                                        <Input value={formatDateFn?.(sprint?.startDate || sprint?.start_date) || '—'} size="large" readOnly style={{ cursor: 'default' }} />
                                    </Form.Item>
                                    <Form.Item label={<PanelSectionLabel style={sectionLabelStyle}>End date</PanelSectionLabel>} style={{ marginBottom: 6 }}>
                                        <Input value={formatDateFn?.(sprint?.endDate || sprint?.end_date) || '—'} size="large" readOnly style={{ cursor: 'default' }} />
                                    </Form.Item>
                                </div>
                                <Form.Item label={<PanelSectionLabel style={sectionLabelStyle}>Description</PanelSectionLabel>} style={{ marginBottom: 8 }}>
                                    <div
                                        style={{
                                            minHeight: 120,
                                            maxHeight: 220,
                                            overflow: 'auto',
                                            border: '1px solid var(--ant-color-border)',
                                            borderRadius: 8,
                                            background: 'var(--ant-color-bg-container)',
                                            padding: '10px 12px',
                                        }}
                                    >
                                        {hasSprintDescription ? (
                                            <div
                                                className="sprint-detail-description-html"
                                                onClick={(e) => {
                                                    const img = e.target?.tagName?.toLowerCase() === 'img' ? e.target : null;
                                                    if (img) {
                                                        const src = img.getAttribute('src');
                                                        if (src) {
                                                            setDescriptionPreviewImage(src);
                                                            return;
                                                        }
                                                    }
                                                    const a = e.target?.closest?.('a');
                                                    const href = a?.getAttribute?.('href');
                                                    if (!href) return;
                                                    if (/^#attachment-\d+$/i.test(href)) {
                                                        e.preventDefault();
                                                        const idx = Number(href.replace('#attachment-', ''));
                                                        const att = sprintAttachments[idx];
                                                        if (!att) return;
                                                        const raw = att?.url || '';
                                                        const resolved = resolveAttachmentUrl(raw);
                                                        if (isLikelyImageAttachmentUrl(raw)) {
                                                            setDescriptionPreviewImage(resolved);
                                                            return;
                                                        }
                                                        const targetHref =
                                                            String(att?.type || '').toLowerCase() === 'link'
                                                                ? normalizePhaseLinkUrl(raw)
                                                                : resolved;
                                                        window.open(targetHref, '_blank', 'noopener,noreferrer');
                                                        return;
                                                    }
                                                    if (/^https?:\/\//i.test(href)) {
                                                        e.preventDefault();
                                                        window.open(href, '_blank', 'noopener,noreferrer');
                                                    }
                                                }}
                                            >
                                                <Markdown
                                                    source={sprintDescriptionText}
                                                    variant="compact"
                                                    linkPrimary
                                                    richInlineAnchors
                                                    attachmentItems={sprintAttachments}
                                                    onLinkClick={(payload) => {
                                                        if (payload.isImage && payload.href) {
                                                            payload.event.preventDefault();
                                                            setDescriptionPreviewImage(payload.href);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <span style={{ color: 'var(--ant-color-text-tertiary)' }}>No description</span>
                                        )}
                                    </div>
                                </Form.Item>
                                <div
                                    style={{
                                        border: '1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.08))',
                                        borderRadius: 8,
                                        padding: '10px 12px',
                                        marginBottom: hasSprintAttachments ? 10 : 0,
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>Sprint Deadline</span>
                                        <span style={{ fontSize: 13 }}>{deadlineText}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, minWidth: 90 }}>Progress</span>
                                        <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.08))' }}>
                                            <div
                                                style={{
                                                    width: `${Math.max(0, Math.min(100, Number(progressPercent) || 0))}%`,
                                                    height: '100%',
                                                    borderRadius: 999,
                                                    background: 'linear-gradient(90deg, #7C3AED 0%, #9F67FF 100%)',
                                                }}
                                            />
                                        </div>
                                        <span style={{ fontSize: 12, fontWeight: 600 }}>{progressPercent}%</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>Status</span>
                                        <StatusBadge status={sprint?.status} size="xs">
                                            {formatSprintStatusLabel(sprint?.status)}
                                        </StatusBadge>
                                    </div>
                                </div>
                                {hasSprintAttachments ? (
                                    <div style={{ marginBottom: 0 }}>
                                        {sprintLinks.length > 0 ? (
                                            <div style={{ marginBottom: sprintFiles.length > 0 ? 12 : 0 }}>
                                                <PanelSectionLabel style={sectionLabelStyle}>
                                                    Links
                                                </PanelSectionLabel>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                    {sprintLinks.map((item, idx) => (
                                                        <a
                                                            key={`sprint-link-${idx}-${item.url}`}
                                                            href={normalizePhaseLinkUrl(item.url)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                                textDecoration: 'none',
                                                                border: '1px solid rgba(124,58,237,0.22)',
                                                                background: 'var(--ant-color-primary-bg, rgba(124,58,237,0.08))',
                                                                color: 'var(--ant-color-primary, #7c3aed)',
                                                                borderRadius: 999,
                                                                padding: '6px 10px',
                                                                fontSize: 13,
                                                            }}
                                                        >
                                                            {item.title || item.url}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}
                                        {sprintFiles.length > 0 ? (
                                            <div>
                                                <PanelSectionLabel style={sectionLabelStyle}>
                                                    Attachments
                                                </PanelSectionLabel>
                                                <div
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))',
                                                        gap: 10,
                                                        width: '100%',
                                                        justifyItems: 'start',
                                                    }}
                                                >
                                                    {sprintFiles.map((item, idx) => {
                                                        const rawUrl = item?.url || '';
                                                        const href = resolveAttachmentUrl(rawUrl);
                                                        const title = item?.title || rawUrl || 'Attachment';
                                                        const isImage = isLikelyImageAttachmentUrl(rawUrl);
                                                        const ext = getAttachmentFileExtension(rawUrl)?.toLowerCase?.() || '';
                                                        return (
                                                            <TaskAttachmentTile
                                                                key={`sprint-file-${idx}-${rawUrl}`}
                                                                href={href}
                                                                title={title}
                                                                isImage={isImage}
                                                                ext={ext}
                                                                onPreviewImage={(url) => setDescriptionPreviewImage(url)}
                                                                onOpenFile={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </Form>
                        );
                    })()}
                </Container>
                <Image
                    style={{ display: 'none' }}
                    src={descriptionPreviewImage || undefined}
                    preview={{
                        visible: !!descriptionPreviewImage,
                        src: descriptionPreviewImage || undefined,
                        onVisibleChange: (visible) => {
                            if (!visible) setDescriptionPreviewImage('');
                        },
                    }}
                />
            </AppModal>
        </Container>
    );
}
